/**
 * Responsibility: finding cards worth grading, and nothing else.
 *
 * This page used to carry six tabs: a board per era, a watch list per era
 * and a bin per era. Four of those were bookkeeping about cards already
 * looked at, which made the page as much about managing lists as about
 * discovery. Watching has its own page now; the bin stays, because it is
 * the memory of what this board has already been told to stop showing.
 *
 * Era moved from a tab to a filter. It is a property of a card, not a place
 * a card lives, and as a tab it doubled the number of lists to hold in your
 * head for no gain.
 *
 * What is being screened, and what by, is said in badges rather than a
 * caption. The caption was a run-on of four separate numbers, none of them
 * findable at a glance.
 *
 * **Scope, stated plainly.** The board screens a PSA population report that
 * covers Pokemon. Narrowing by sport or by any other catalogue needs a
 * different source behind it than the one this reads, so there is no
 * control for it - a menu that can only ever return nothing is worse than
 * its absence. Finding a sports card by name is what the Search page does.
 */
import { useMemo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { MetricCard, PageContainer, PageTabs } from '@kit/components';
import { ArchiveTable } from '../components/grading/ArchiveTable';
import { CardDetailDialog } from '../components/grading/CardDetailDialog';
import { DiscoveryBadges } from '../components/grading/DiscoveryBadges';
import { DiscoveryFilterButton } from '../components/grading/DiscoveryFilterButton';
import { DEFAULT_FILTERS } from '../components/grading/discoveryFilters';
import type { DiscoveryFilters } from '../components/grading/discoveryFilters';
import { DiscoveryTable } from '../components/grading/DiscoveryTable';
import { OutlierBadges, OutlierControls } from '../components/grading/OutlierControls';
import { CatalogueBadges, CatalogueControls } from '../components/grading/CatalogueControls';
import { CatalogueTable } from '../components/grading/CatalogueTable';
import { MarketCardDialog } from '../components/market/MarketCardDialog';
import { CardTagDialog, type TaggableCard } from '../components/collection/CardTagDialog';
import { OutlierTable } from '../components/grading/OutlierTable';
import { gradeBucketLabel } from '../components/grading/gradeBuckets';
import { hubApi } from '../api/client';
import {
  cardDetailKey,
  useArchiveCard,
  useDiscoveryArchive,
  useDiscoveryBoard,
  useRefreshDiscovery,
  useRestoreCard,
} from '../api/hooks/useDiscovery';
import {
  useBuildCatalogue,
  useCatalogueBoard,
  useCatalogues,
  useLikeDiscovered,
  useMarketCard,
  useOutlierScan,
  useSaved,
  useStandingOutliers,
  useSweepOutliers,
  useToggleLike,
  useToggleWatch,
} from '../api/hooks/useMarket';
import { money, percent } from '../components/grading/money';
import { FittedTable } from '../components/shelf/FittedTable';
import { useFillHeight } from '../hooks/useFillHeight';
import { usePageState } from '../hooks/usePageState';
import { useDetailQueue } from '../hooks/useDetailQueue';
import type { DiscoveredCard } from '../api/client';

/**
 * Two algorithms and one bin.
 *
 * Arbitrage screens a population report and asks whether the gap between
 * raw and graded is worth crossing - a bet on a grading outcome months
 * away. Catalogue asks the same question of sports and every other
 * trading card game, from the price catalogue instead, because the
 * population report covers Pokemon and nothing else. Outliers asks
 * whether somebody is asking less than the last few people got, which is
 * a flip you can act on today. They share a page because all three
 * answer "what should I buy", and nothing else.
 */
/** Cards read per scan. Matches SCAN_LIMIT on the hub. */
const OUTLIER_SCAN_SIZE = 12;

const TABS = ['Arbitrage', 'Catalogue', 'Outliers', 'Bin'] as const;

/** Matches a row against the keyword box, on the two names it has. */
function matchesKeywords(row: DiscoveredCard, keywords: string): boolean {
  const wanted = keywords.trim().toLowerCase();
  if (wanted === '') return true;
  const haystack = `${row.card_name} ${row.set_name} ${row.card_number}`.toLowerCase();
  // Every word must appear, so a second word narrows rather than widens.
  return wanted.split(/[\s,]+/).every((word) => haystack.includes(word));
}

export function DiscoveryPage() {
  const [tabIndex, setTabIndex] = usePageState('discovery.tab', 0, 'durable');
  const [filters, setFilters] = usePageState<DiscoveryFilters>(
    'discovery.filters',
    DEFAULT_FILTERS,
    'durable',
  );
  const [detail, setDetail] = usePageState<DiscoveredCard | null>('discovery.open', null);
  // Liked state is keyed on the catalogue product, which a board row does
  // not carry - the hub resolves the match when the heart is clicked. So
  // this remembers what was liked in this sitting, and a card liked last
  // week shows an empty heart. Liking twice is harmless: the store keys on
  // the product and upserts.
  const [likedNow, setLikedNow] = usePageState<string[]>('discovery.liked', []);
  // The scan is expensive, so the box holds a draft and only a submitted
  // phrase becomes a query.
  const [scanQuery, setScanQuery] = usePageState<string | null>('discovery.scan', null);
  const [bucket, setBucket] = usePageState('discovery.bucket', '');
  const [catalogue, setCatalogue] = usePageState('discovery.catalogue', 'baseball', 'durable');
  // A catalogue row is a catalogue product, not a population record, so
  // it opens the same dialog a search result does.
  const [marketKey, setMarketKey] = usePageState<string | null>('discovery.market', null);

  const theme = useTheme();
  const onDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Measured, not guessed: the room under the table changes when the
  // figures above it are hidden, and a constant allowance cannot know.
  const [fillRef, tableHeight] = useFillHeight();

  const onCatalogue = tabIndex === 1;
  const onOutliers = tabIndex === 2;
  const onBin = tabIndex === 3;
  const { era, language, floor } = filters;

  const board = useDiscoveryBoard(era, floor, language);
  const archive = useDiscoveryArchive(era, 'BINNED');
  const bin = useArchiveCard(era, floor, language);
  const restore = useRestoreCard(era);
  const refresh = useRefreshDiscovery(era, floor, language);
  const like = useLikeDiscovered();
  const scan = useOutlierScan(onOutliers ? scanQuery : null);
  // The standing list is what the tab opens on: the hourly sweep of the
  // cards already cared about, ready without asking for anything.
  const standing = useStandingOutliers();
  const sweep = useSweepOutliers();
  const catalogues = useCatalogues();
  const catalogueBoard = useCatalogueBoard(catalogue, filters.keywords, '');
  const buildCatalogue = useBuildCatalogue();
  const marketCard = useMarketCard(marketKey);
  // The catalogue board carries catalogue product keys, so the same two
  // decisions the search page offers apply to it unchanged.
  const saved = useSaved();
  const likedKeys = new Set((saved.data?.likes ?? []).map((row) => row.product_key));
  const watchedKeys = new Set((saved.data?.watching ?? []).map((row) => row.product_key));
  const toggleLike = useToggleLike(likedKeys);
  // Same offer as the search page makes: a card just kept is the moment
  // to say which run it is for.
  const [filing, setFiling] = useState<TaggableCard | null>(null);
  const likeThen = (key: string, name: string) => {
    const wasLiked = likedKeys.has(key);
    toggleLike.mutate(key);
    if (!wasLiked) setFiling({ key, name, kept: true, tags: [] });
  };
  const toggleWatch = useToggleWatch(watchedKeys);

  // Memoised against the response rather than rebuilt each render: the
  // prefetch queue keys off this list, and a fresh array every render
  // would restart the queue continuously and never warm anything.
  const matched = useMemo(() => board.data?.rows ?? [], [board.data]);
  const rows = useMemo(
    () => matched.filter((row) => matchesKeywords(row, filters.keywords)),
    [matched, filters.keywords],
  );

  // Warm every row's detail in the background so opening one is instant.
  // A click promotes that card to the front rather than queueing behind
  // the rest of the board.
  const detailKeys = useMemo(() => rows.map((row) => row.discovery_key), [rows]);
  const { promote } = useDetailQueue({
    keys: detailKeys,
    queryKey: cardDetailKey,
    fetch: hubApi.discoveryCard,
    enabled: !onBin && !onOutliers && !onCatalogue && rows.length > 0,
  });

  const busy = bin.isPending || restore.isPending || refresh.isPending || like.isPending;
  const verified = rows.filter((row) => row.verified).length;
  const suspect = rows.filter((row) => row.reference_disagrees || !row.still_affordable).length;
  const profitable = rows.filter((row) => Number(row.expected_profit ?? '0') > 0).length;
  const error =
    board.error ?? archive.error ?? like.error ?? scan.error ?? buildCatalogue.error;

  // A typed scan takes over when there is one; otherwise the standing
  // sweep. Both are the same rows, so the table and filter do not care.
  const outlierSource = scanQuery ? scan.data : standing.data;
  const outliers = useMemo(() => outlierSource?.rows ?? [], [outlierSource]);
  const shownOutliers = useMemo(
    () => (bucket === '' ? outliers : outliers.filter((row) => row.bucket === bucket)),
    [outliers, bucket],
  );

  const openCard = (row: DiscoveredCard) => {
    promote(row.discovery_key);
    setDetail(row);
  };

  return (
    <PageContainer
      disableBodyScroll={onDesktop}
      tabs={
        <PageTabs
          tabs={TABS.map((label, index) => ({ value: index, label }))}
          value={tabIndex}
          onChange={setTabIndex}
        />
      }
    >
      <Stack spacing={1.5} sx={{ minWidth: 0, height: '100%' }}>
        {error ? <Alert severity="error">{(error as Error).message}</Alert> : null}

        {board.data && !board.data.ready ? (
          <Alert severity="info">
            No population data loaded yet. Hit <strong>Refresh</strong> to pull the report - it
            is one large file and takes about a minute.
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            flexShrink: 0,
          }}
        >
          {onCatalogue ? (
            <>
              <MetricCard
                label="Clearing costs"
                value={String(catalogueBoard.data?.passed ?? 0)}
                helper="best case, no gem rate here"
              />
              <MetricCard
                label="Best payoff"
                value={
                  catalogueBoard.data?.rows.length
                    ? money(catalogueBoard.data.rows[0].best_case_profit)
                    : '—'
                }
                helper="if one copy grades a 10"
              />
              <MetricCard
                label="Universe"
                value={(catalogueBoard.data?.universe_size ?? 0).toLocaleString()}
                helper="cards read from set pages"
              />
              <MetricCard
                label="Built"
                value={
                  catalogueBoard.data?.built_at
                    ? catalogueBoard.data.built_at.slice(0, 10)
                    : 'never'
                }
                helper="prices move daily"
              />
            </>
          ) : onOutliers ? (
            <>
              <MetricCard
                label="Under market"
                value={String(shownOutliers.length)}
                helper={bucket === '' ? 'every grade' : gradeBucketLabel(bucket)}
              />
              <MetricCard
                label="Deepest"
                value={
                  shownOutliers.length > 0 ? percent(shownOutliers[0].discount, 0) : '—'
                }
                helper="under the last five sales"
              />
              <MetricCard
                label="Grades hit"
                value={String(outlierSource?.buckets.length ?? 0)}
                helper="raw and slabs alike"
              />
              <MetricCard
                label="Cards read"
                value={
                  scanQuery ? String(OUTLIER_SCAN_SIZE) : String(standing.data?.watched ?? 0)
                }
                helper={scanQuery ? 'read for this scan' : 'your own cards, swept hourly'}
              />
            </>
          ) : (
            <>
              <MetricCard
                label="On this board"
                value={String(rows.length)}
                helper={`of ${board.data?.total_candidates ?? 0} passing`}
              />
              <MetricCard
                label="Pay on average"
                value={String(profitable)}
                helper="at own gem rate"
              />
              <MetricCard label="Verified" value={String(verified)} helper="cost from TCGplayer" />
              <MetricCard label="Flagged" value={String(suspect)} helper="price looks wrong" />
            </>
          )}
        </Box>

        {/* **The badges and the controls share a line.** They are the
            same subject read two ways - the badges say what is on the
            board, the buttons change it - and on a laptop the board is
            what the page is short of, so a row spent on two buttons is a
            row of cards not seen. They split on a phone, where a row
            cannot hold both. */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ flexShrink: 0 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {onCatalogue ? (
              <CatalogueBadges
                board={catalogueBoard.data ?? null}
                label={catalogues.data?.find((entry) => entry.key === catalogue)?.label ?? catalogue}
                keywords={filters.keywords}
                onClearKeywords={() => setFilters({ ...filters, keywords: '' })}
              />
            ) : onOutliers ? (
              <OutlierBadges
                query={scanQuery ? (scan.data?.query ?? null) : null}
                sweptAt={scanQuery ? null : (standing.data?.swept_at ?? null)}
                watched={standing.data?.watched ?? 0}
                shown={shownOutliers.length}
                found={outliers.length}
                bucket={bucket}
                onBucket={setBucket}
              />
            ) : (
              <DiscoveryBadges
                filters={filters}
                onChange={setFilters}
                shown={onBin ? (archive.data?.rows.length ?? 0) : rows.length}
                matched={matched.length}
                candidates={board.data?.total_candidates ?? 0}
                universeSize={board.data?.universe_size ?? 0}
                universeBuilt={board.data?.universe_built ?? null}
              />
            )}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ flexShrink: 0, ml: { md: 'auto' } }}
          >
            {onCatalogue ? (
              <CatalogueControls
                catalogues={catalogues.data ?? []}
                chosen={catalogue}
                onChosen={setCatalogue}
                keywords={filters.keywords}
                onKeywords={(next) => setFilters({ ...filters, keywords: next })}
                onBuild={() => buildCatalogue.mutate(catalogue)}
                building={buildCatalogue.isPending}
                ready={catalogueBoard.data?.ready ?? false}
              />
            ) : onOutliers ? (
              <OutlierControls
                onScan={setScanQuery}
                initial={scanQuery}
                bucket={bucket}
                onBucket={setBucket}
                buckets={outlierSource?.buckets ?? []}
                busy={scan.isFetching || sweep.isPending}
                onSweep={() => {
                  setScanQuery(null);
                  sweep.mutate();
                }}
                watched={standing.data?.watched ?? 0}
                onClearScan={() => setScanQuery(null)}
              />
            ) : (
              <>
                <DiscoveryFilterButton
                  filters={filters}
                  onChange={setFilters}
                  offeredFloors={board.data?.available_floors ?? [5, 10, 25, 50]}
                  busy={busy}
                />

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refresh.mutate()}
                  disabled={busy}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {refresh.isPending ? 'Polling' : 'Refresh'}
                </Button>
              </>
            )}
          </Stack>
        </Stack>

        <FittedTable fillRef={fillRef}>
          {onCatalogue ? (
            <CatalogueTable
              rows={catalogueBoard.data?.rows ?? []}
              loading={catalogueBoard.isFetching || buildCatalogue.isPending}
              onOpen={(row) => setMarketKey(row.product_key)}
              onLike={(row) => likeThen(row.product_key, row.name ?? row.product_key)}
              onWatch={(row) => toggleWatch.mutate(row.product_key)}
              liked={likedKeys}
              watched={watchedKeys}
              maxHeight={tableHeight}
            />
          ) : onOutliers ? (
            <OutlierTable
              rows={shownOutliers}
              loading={scan.isFetching || sweep.isPending || standing.isLoading}
              maxHeight={tableHeight}
            />
          ) : onBin ? (
            <ArchiveTable
              rows={archive.data?.rows ?? []}
              loading={archive.isLoading}
              busy={busy}
              onRestore={(row) => restore.mutate(row.discovery_key)}
              maxHeight={tableHeight}
            />
          ) : (
            <DiscoveryTable
              rows={rows}
              loading={board.isLoading}
              busy={busy}
              onArchive={(row) => bin.mutate({ discoveryKey: row.discovery_key, intent: 'BINNED' })}
              onWatch={(row) => bin.mutate({ discoveryKey: row.discovery_key, intent: 'WATCHING' })}
              onLike={(row) => {
                like.mutate(row.discovery_key);
                setLikedNow((keys) =>
                  keys.includes(row.discovery_key) ? keys : [...keys, row.discovery_key],
              );
              }}
              liked={new Set(likedNow)}
              onOpen={openCard}
              maxHeight={tableHeight}
            />
          )}
        </FittedTable>
      </Stack>

      <CardTagDialog card={filing} justLiked onClose={() => setFiling(null)} />

      <MarketCardDialog
        card={marketCard.data?.result ?? null}
        // Unlike search and likes, this page has no card in hand when a
        // row is clicked - only its key - so the dialog has to open on
        // the click rather than on the answer. A sports card that has
        // never been read takes seconds behind the catalogue's pacing.
        pending={marketKey !== null}
        onClose={() => setMarketKey(null)}
        onAdd={() => setMarketKey(null)}
      />

      <CardDetailDialog
        card={detail}
        onClose={() => setDetail(null)}
        onArchive={(card) => {
          bin.mutate({ discoveryKey: card.discovery_key, intent: 'BINNED' });
          setDetail(null);
        }}
      />
    </PageContainer>
  );
}
